import { AdminLayout, useFeedback, useTranslation } from "@opendash/core";
import { Icon } from "@opendash/icons";
import { useParams } from "@opendash/router";
import * as turf from "@turf/turf";
import {
  Button,
  Divider,
  Modal,
  Space,
  Table,
  Tooltip,
  Typography,
} from "antd";
import { ColumnsType } from "antd/lib/table";
import dayjs from "dayjs";
import L from "leaflet/dist/leaflet";
import { observer } from "mobx-react-lite";
import Parse from "parse";
import React, { useEffect, useState } from "react";
import { StopDetail } from "../../components/StopDetail";
import { StopTransfer } from "../../components/StopTransfer";
import { TripCreateModal } from "../../components/TripCreateModal";
import { TripUpdateModal } from "../../components/TripUpdateModal";
import UserGuide from "../../components/UI/UserGuide";
import { Calendar, Route, Stop_Times, Trip } from "../../js/parsegenerated";
import { TourId } from "../../types/TourId";
interface TripDataType {
  key: string;
  name: string;
  description: string;
  id?: string; // for the user guide pseudo trip only
}

export const TripsPage = observer(() => {
  const params = useParams();
  const t = useTranslation();
  const { message } = useFeedback();
  const { confirm } = Modal;
  const [hideRoute, setHideRoute] = useState<boolean[]>([]);
  const [shownLines, setShownLines] = useState<string>("Alle");
  const [tripsRecord, setTripsRecord] = useState<TripDataType[]>([]);
  const [stopsRecord, setStopsRecord] = useState<Record<
    string,
    Stop_Times[]
  > | null>(null); // TODO: Type
  const [createModalOpen, setCreateModalOpen] = useState<boolean>(false);
  const [updateModalOpen, setUpdateModalOpen] = useState<boolean>(false);
  const [selectedTrip, setSelectedTrip] = useState<string>("");
  const [reload, setReload] = useState<number>(0);
  const map = React.useRef(null);

  const container = React.useRef(null);

  const columns: ColumnsType<TripDataType> = [
    {
      title: t("gtfs:trip.table.columns.name.label"),
      dataIndex: "name",
      render: (_, record) => <span>{record.name}</span>,
      sorter: (a, b) => (b.id?.length ?? 0) - (a.id?.length ?? 0),
      defaultSortOrder: "ascend",
    },
    {
      title: t("gtfs:trip.table.columns.description.label"),
      dataIndex: "description",
      render: (_, record) => <span>{record.description}</span>,
    },
    {
      title: t("gtfs:trip.table.columns.action.label"),
      dataIndex: "action",
      width: 150,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title={t("gtfs:trip.table.columns.action.delete.tooltip")}>
            <Button
              shape="circle"
              onClick={() => {
                confirm({
                  title: t(
                    "gtfs:trip.table.columns.action.delete.confirm.title"
                  ),
                  icon: <Icon icon="fa:exclamation"></Icon>,
                  content: t(
                    "gtfs:trip.table.columns.action.delete.confirm.content"
                  ),
                  okText: t("gtfs:global.button.delete_label"),
                  okType: "danger",
                  cancelText: t("gtfs:global.button.cancel_label"),
                  onOk() {
                    deleteconfirm(record.key);
                  },
                  onCancel() {},
                });
              }}
              icon={
                <Icon
                  icon="fa:trash"
                  key="delete"
                  style={{ color: "#ff4d4d" }}
                />
              }
            />
          </Tooltip>
          <Tooltip title={t("gtfs:trip.table.columns.action.update.tooltip")}>
            <Button
              shape="circle"
              onClick={() => {
                setSelectedTrip(record.key);
                setUpdateModalOpen(true);
              }}
              icon={<Icon icon="fa:pen"></Icon>}
            />
          </Tooltip>
          <Tooltip title={t("gtfs:trip.table.columns.action.show_map.tooltip")}>
            <Button
              shape="circle"
              onClick={() => {
                setHideRoute((curr) => {
                  const newState = [];
                  for (let key in curr) {
                    newState[key] = curr[key];
                  }
                  newState[record.key] = !newState[record.key];
                  return newState;
                });
              }}
              icon={
                (hideRoute[record.key] && <Icon icon="fa:eye-slash" />) || (
                  <Icon icon="fa:eye" />
                )
              }
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const pseudoTripsRecord = {
    key: TourId.Trip.card,
    name: "Neuer Trip",
    description: "Ein Trip der gewählten Route",
    id: TourId.Trip.card,
  };

  const records = tripsRecord.concat([pseudoTripsRecord]);

  useEffect(() => {
    const initStops = async () => {
      if (tripsRecord && tripsRecord.length > 0) {
        const cStops = {};
        for (const trip of tripsRecord) {
          const stoptimeobjects = await new Parse.Query(Stop_Times)
            .equalTo("trip_id", {
              __type: "Pointer",
              className: Trip.className,
              objectId: trip.key,
            })
            .include("stop_id")
            .ascending("stop_sequence")
            .find();
          cStops[trip.key] = stoptimeobjects;
        }
        setStopsRecord(cStops);
      }
    };
    initStops();
  }, [tripsRecord]);

  const deleteconfirm = async (key: string) => {
    const selectedtrip: Trip = await new Parse.Query(Trip).get(key);

    const service: Calendar = selectedtrip.get("service_id");

    try {
      const savedobject = await selectedtrip.destroy();
      await service.destroy();
      setReload((currVal) => currVal + 1);
      message.success(t("gtfs:global.message.deletesuccess"));
    } catch (error) {
      message.error(t("gtfs:global.message.deleteerror"));
    }
  };

  const onOkCreate = () => {
    setReload((reload) => reload + 1);
    setCreateModalOpen(false);
  };

  const onCancelCreate = () => {
    setCreateModalOpen(false);
  };

  const onOkUpdate = () => {
    setReload((reload) => reload + 1);
    setUpdateModalOpen(false);
  };

  const onCancelUpdate = () => {
    setUpdateModalOpen(false);
  };
  useEffect(() => {
    if (!container.current) {
      return;
    }

    const geoLayers = [];
    if (stopsRecord && Object.keys(stopsRecord).length > 0) {
      // reset all markers and lines
      if (map.current) {
        map.current.eachLayer((layer) => {
          if (layer instanceof L.Marker || layer instanceof L.Polyline) {
            map.current.removeLayer(layer);
          }
        });

        // Remove all connections between markers
        map.current.eachLayer((layer) => {
          if (layer instanceof L.Polyline) {
            map.current.removeLayer(layer);
          }
        });
      }
      const lineString: string[] = [];
      // add markers and lines if not hidden
      Object.keys(stopsRecord).forEach((key) => {
        if (hideRoute[key]) return;
        const trip = tripsRecord.find((trip) => trip.key === key);
        lineString.push(trip?.name);

        if (stopsRecord[key].length > 1) {
          const geo = turf.lineString(
            stopsRecord[key].map((stopTime) => {
              return [
                parseFloat(stopTime.get("stop_id").get("stop_lon")),
                parseFloat(stopTime.get("stop_id").get("stop_lat")),
              ];
            }),
            stopsRecord[key]
          );
          geoLayers.push(geo);
        }
        if (stopsRecord[key].length > 0) {
          const geoPoints = stopsRecord[key].map((stopTime) => {
            const description = `${trip.name} - ${stopTime.get("stop_id").get("stop_name")} - Ank.:${dayjs(stopTime.get("arrival_time")).format("HH:mm")} Abf.:${dayjs(stopTime.get("departure_time")).format("HH:mm")}`;
            const point = turf.point([
              parseFloat(stopTime.get("stop_id").get("stop_lon")),
              parseFloat(stopTime.get("stop_id").get("stop_lat")),
            ]);
            point.properties = {
              description: description,
            };
            return point;
          });
          geoLayers.push(geoPoints);
        }
      });

      if (lineString.length > 0)
        setShownLines(
          lineString.length === tripsRecord.length
            ? "Alle"
            : lineString.join(", ")
        );
      else setShownLines("Keine");
    }

    if (!map.current) {
      map.current = L.map(container.current, {
        center: [0, 0],
        zoom: 1,
        //@ts-ignore
        layers: [L.tileLayer(...window.leafletTiles)],
        gestureHandling: true,
      });

      map.current.scrollWheelZoom.enable();
    }
    geoLayers.forEach((geo) => {
      console.log({ geo });
      const geoJSONFeatureLayer = L.geoJSON(geo, {
        // style: styleFunction,
        // onEachFeature,
        pointToLayer: function (feature, latlng) {
          const marker = L.marker(latlng);
          marker.bindTooltip(feature?.properties?.description);
          return marker;
        },
      }).addTo(map.current);
      map.current.fitBounds(geoJSONFeatureLayer.getBounds());
    });
  }, [container, stopsRecord, hideRoute]);

  useEffect(() => {
    new Parse.Query(Trip)
      .equalTo("route_id", {
        __type: "Pointer",
        className: Route.className,
        objectId: params["id"],
      })
      .include("route_id")
      .find()
      .then((tripsfrompromise) => {
        const tabletrips = tripsfrompromise.map((tripfrompromise) => {
          return {
            key: tripfrompromise.id,
            name:
              tripfrompromise.get("trip_short_name") ??
              tripfrompromise.route_id.get("route_short_name"),
            description:
              tripfrompromise.get("trip_headsign") ??
              tripfrompromise.route_id.get("route_long_name"),
          };
        });
        setTripsRecord(tabletrips);
      });
  }, [reload]);

  return (
    <AdminLayout contentPadding>
      <UserGuide.Trip />
      <Button
        id={TourId.Trip.create}
        type="primary"
        onClick={() => {
          setCreateModalOpen(true);
        }}
        style={{
          marginRight: 10,
          marginBottom: 10,
          float: "right",
        }}
      >
        {t("gtfs:trip.table.columns.action.create.label")}
      </Button>
      <Divider></Divider>
      <Typography.Title level={3}>
        {t("gtfs:trip.map.overview.title", { lines: shownLines })}
      </Typography.Title>
      <div ref={container} style={{ width: "100%", height: "300px" }}></div>
      <Divider></Divider>
      <Table
        id={TourId.Trip.overview}
        columns={columns}
        dataSource={records}
        size="middle"
        rowKey={(record: TripDataType) => record.key}
        onRow={(record) => {
          return {
            id: record.id ?? undefined,
            style: { display: record.id ? "none" : "" },
          };
        }}
        expandable={{
          expandIcon: ({ expanded, onExpand, record }) => {
            return (
              <a
                onClick={(e) => {
                  onExpand(record, e);
                }}
              >
                {expanded ? (
                  <Icon icon="fa:angle-down"></Icon>
                ) : (
                  <Icon icon="fa:angle-right"></Icon>
                )}
              </a>
            );
          },
          expandedRowRender: (record) => {
            return (
              <div id={record.id ? TourId.Trip.stops : undefined}>
                <StopDetail id={record.key} />
                <StopTransfer id={record.key} />
              </div>
            );
          },
          rowExpandable: (record) => {
            return true;
          },
        }}
      />

      <TripCreateModal
        open={createModalOpen}
        route_id={params["id"]}
        onOk={onOkCreate}
        onCancel={onCancelCreate}
      />

      <TripUpdateModal
        open={updateModalOpen}
        trip_id={selectedTrip}
        route_id={params["id"]}
        onOk={onOkUpdate}
        onCancel={onCancelUpdate}
      />
    </AdminLayout>
  );
});
